import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import type { ComponentLike } from '@glint/template';

/**
 * Color theme options for the tool card
 */
export type ToolCardColor = 'indigo' | 'teal' | 'amber' | 'emerald' | 'violet' | 'blue' | 'pink';

/**
 * ToolCard - A gradient-styled navigation card for financial tools
 *
 * Similar to QuickActionLink but with gradient backgrounds and
 * a more prominent visual style for tool sections.
 *
 * @example
 * ```gts
 * <ToolCard
 *   @route="authenticated.repaying-student-debt.coaching"
 *   @icon={{PhChatCircleDots}}
 *   @title="Loan Coaches"
 *   @subtitle="Expert guidance"
 *   @color="teal"
 * />
 * ```
 */
interface ToolCardSignature {
  Args: {
    /** Route to navigate to */
    route: string;
    /** Phosphor icon component */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text */
    title: string;
    /** Subtitle text */
    subtitle?: string;
    /** Color theme for gradient and icon */
    color?: ToolCardColor;
  };
  Element: HTMLAnchorElement;
}

const colorClasses: Record<
  ToolCardColor,
  { gradient: string; iconBg: string; iconText: string; border: string }
> = {
  indigo: {
    gradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30',
    iconBg: 'bg-indigo-100 dark:bg-indigo-900/50',
    iconText: 'text-indigo-600 dark:text-indigo-400',
    border: 'border-indigo-100 dark:border-indigo-800',
  },
  teal: {
    gradient: 'from-teal-50 to-cyan-50 dark:from-teal-900/30 dark:to-cyan-900/30',
    iconBg: 'bg-teal-100 dark:bg-teal-900/50',
    iconText: 'text-teal-600 dark:text-teal-400',
    border: 'border-teal-100 dark:border-teal-800',
  },
  amber: {
    gradient: 'from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30',
    iconBg: 'bg-amber-100 dark:bg-amber-900/50',
    iconText: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-100 dark:border-amber-800',
  },
  emerald: {
    gradient: 'from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30',
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/50',
    iconText: 'text-emerald-600 dark:text-emerald-400',
    border: 'border-emerald-100 dark:border-emerald-800',
  },
  violet: {
    gradient: 'from-violet-50 to-purple-50 dark:from-violet-900/30 dark:to-purple-900/30',
    iconBg: 'bg-violet-100 dark:bg-violet-900/50',
    iconText: 'text-violet-600 dark:text-violet-400',
    border: 'border-violet-100 dark:border-violet-800',
  },
  blue: {
    gradient: 'from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30',
    iconBg: 'bg-blue-100 dark:bg-blue-900/50',
    iconText: 'text-blue-600 dark:text-blue-400',
    border: 'border-blue-100 dark:border-blue-800',
  },
  pink: {
    gradient: 'from-pink-50 to-rose-50 dark:from-pink-900/30 dark:to-rose-900/30',
    iconBg: 'bg-pink-100 dark:bg-pink-900/50',
    iconText: 'text-pink-600 dark:text-pink-400',
    border: 'border-pink-100 dark:border-pink-800',
  },
};

const getColorClasses = (color: ToolCardColor = 'indigo') => colorClasses[color];

const ToolCard: TOC<ToolCardSignature> = <template>
  {{#let (getColorClasses @color) as |colors|}}
    <LinkTo
      @route={{@route}}
      class="flex flex-col items-center gap-2 p-4 bg-gradient-to-br
        {{colors.gradient}}
        rounded-xl hover:shadow-md transition-all border
        {{colors.border}}
        text-center"
      ...attributes
    >
      <div class="p-3 {{colors.iconBg}} rounded-xl">
        {{#let @icon as |Icon|}}
          <Icon @size={{24}} class={{colors.iconText}} />
        {{/let}}
      </div>
      <span class="text-sm font-medium text-gray-900 dark:text-white">{{@title}}</span>
      {{#if @subtitle}}
        <span class="text-xs text-gray-500 dark:text-gray-400">{{@subtitle}}</span>
      {{/if}}
    </LinkTo>
  {{/let}}
</template>;

export default ToolCard;
